<template>
  <div class="filter-container">
    <!-- Search Section -->
    <v-card flat outlined class="filter-card mb-3">
      <v-card-text class="pa-3">
        <!-- Filter Toggle Bar -->
        <v-row align="center" class="mb-2" v-if="!exportation">
          <v-col cols="12" class="py-1">
            <!-- Prominent toggle bar when filters are hidden -->
            <div
              :class="[
                'filter-toggle-bar',
                'd-flex align-center justify-space-between',
                { 'filter-toggle-bar--collapsed': !show_filter },
              ]"
              @click="!show_filter ? showFilters() : null"
            >
              <div class="d-flex align-center">
                <v-btn
                  :text="show_filter"
                  :outlined="!show_filter"
                  small
                  :color="show_filter ? 'primary' : 'primary'"
                  @click.stop="showFilters"
                  class="px-3 text-none"
                  :class="{ 'filter-toggle-btn--collapsed': !show_filter }"
                >
                  <v-icon
                    left
                    small
                    :class="{ 'filter-icon-pulse': !show_filter && getTotalActiveFilters() > 0 }"
                    >{{ show_filter ? 'mdi-chevron-up' : 'mdi-filter-variant' }}</v-icon
                  >
                  {{ show_filter ? 'Hide Filters' : 'Show Filters' }}
                  <v-icon v-if="!show_filter" right small class="ml-1">mdi-chevron-down</v-icon>
                </v-btn>

                <v-chip
                  v-if="getTotalActiveFilters() > 0"
                  small
                  :color="show_filter ? 'success' : 'primary'"
                  text-color="white"
                  class="ml-2"
                >
                  <v-icon x-small left>mdi-filter</v-icon>
                  {{ getTotalActiveFilters() }} active
                </v-chip>
              </div>
              <div class="d-flex align-center">
                <v-tooltip bottom v-if="getTotalActiveFilters() > 0">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      text
                      small
                      color="error"
                      @click.stop="resetDashboard"
                      class="px-2 text-none"
                    >
                      <v-icon left small>mdi-filter-remove</v-icon>
                      Clear All
                    </v-btn>
                  </template>
                  <span>Remove all active filters</span>
                </v-tooltip>
              </div>
            </div>

            <!-- Collapsed Active Filters Summary -->
            <div
              v-if="!show_filter && getTotalActiveFilters() > 0"
              class="collapsed-filters-summary mt-2"
            >
              <div class="d-flex align-center flex-wrap">
                <v-icon x-small color="grey darken-1" class="mr-1">mdi-filter-check</v-icon>
                <span class="text-caption grey--text text--darken-1 mr-2 font-weight-medium"
                  >Active:</span
                >
                <v-chip
                  v-if="search_filter.key"
                  x-small
                  outlined
                  color="primary"
                  class="ma-1"
                  close
                  @click:close="
                    search_filter.key = ''
                    filterChanged()
                  "
                >
                  Search: {{ search_filter.key | truncate(15) }}
                </v-chip>
                <v-chip v-if="search_filter.sort" x-small outlined color="primary" class="ma-1">
                  {{ search_filter.sort }}
                </v-chip>
                <v-chip
                  v-if="search_filter.brands.length > 0"
                  x-small
                  outlined
                  color="primary"
                  class="ma-1"
                >
                  Brands ({{ search_filter.brands.length }})
                </v-chip>
                <v-chip
                  v-if="search_filter.categories.length > 0"
                  x-small
                  outlined
                  color="primary"
                  class="ma-1"
                >
                  Categories ({{ search_filter.categories.length }})
                </v-chip>
                <v-chip
                  v-if="search_filter.availabilities.length > 0"
                  x-small
                  outlined
                  color="primary"
                  class="ma-1"
                >
                  Availability ({{ search_filter.availabilities.length }})
                </v-chip>
                <v-chip
                  v-if="search_filter.competitors_websites.length > 0"
                  x-small
                  outlined
                  color="primary"
                  class="ma-1"
                >
                  Competitors ({{ search_filter.competitors_websites.length }})
                </v-chip>
                <v-chip v-if="search_filter.matched" x-small outlined color="primary" class="ma-1">
                  {{ search_filter.matched }}
                </v-chip>
                <v-chip
                  v-if="search_filter.coefficient"
                  x-small
                  outlined
                  color="primary"
                  class="ma-1"
                >
                  {{ search_filter.coefficient }}
                </v-chip>
                <v-chip v-if="search_filter.source" x-small outlined color="primary" class="ma-1">
                  {{ search_filter.source }}
                </v-chip>
                <v-chip v-if="search_filter.changes" x-small outlined color="primary" class="ma-1">
                  {{ search_filter.changes }}
                </v-chip>
                <v-chip
                  v-if="
                    search_filter.price_index.min !== null || search_filter.price_index.max !== null
                  "
                  x-small
                  outlined
                  color="primary"
                  class="ma-1"
                >
                  Index: {{ search_filter.price_index.min || '*' }} -
                  {{ search_filter.price_index.max || '*' }}
                </v-chip>
                <v-chip
                  v-if="
                    search_filter.similarity_score_min !== null &&
                    search_filter.similarity_score_min !== ''
                  "
                  x-small
                  outlined
                  color="primary"
                  class="ma-1"
                  close
                  @click:close="
                    search_filter.similarity_score_min = null
                    filterChanged()
                  "
                >
                  Similarity &ge; {{ search_filter.similarity_score_min }}
                </v-chip>
                <template v-for="(vals, key) in search_filter.custom_fields">
                  <v-chip
                    v-if="vals && vals.length > 0"
                    :key="'cf-chip-' + key"
                    x-small
                    outlined
                    color="primary"
                    class="ma-1"
                    close
                    @click:close="
                      $set(search_filter.custom_fields, key, [])
                      filterChanged()
                    "
                  >
                    {{ formatFieldKey(key) }} ({{ vals.length }})
                  </v-chip>
                </template>
                <v-btn text x-small color="primary" class="text-none ml-1" @click="showFilters">
                  Edit filters
                  <v-icon right x-small>mdi-pencil</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Hint text when no filters active and panel hidden -->
            <div
              v-if="!show_filter && getTotalActiveFilters() === 0"
              class="collapsed-filters-hint mt-2"
              @click="showFilters"
            >
              <v-icon x-small color="grey" class="mr-1">mdi-filter-plus-outline</v-icon>
              <span class="text-caption grey--text"
                >Click to expand and add filters for brands, categories, availability, competitors
                and more</span
              >
            </div>
          </v-col>
        </v-row>

        <!-- Search Bar -->
        <v-row align="center">
          <v-col cols="12">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-bind="attrs"
                  v-on="on"
                  v-model="search_filter.key"
                  @change="filterChanged"
                  @keyup.enter="sumbitFilter"
                  :label="!exportation ? 'Search products by name, brand, category...' : 'Name'"
                  hide-details
                  clearable
                  color="primary"
                  outlined
                  dense
                >
                  <template v-slot:append v-if="search_filter.key">
                    <v-icon color="grey" size="large" class="mt-1">mdi-keyboard-return</v-icon>
                  </template>
                </v-text-field>
              </template>
              <span>Type to search and press Enter to apply</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <!-- Main Filters Section -->
    <v-expand-transition>
      <v-card v-show="show_filter || exportation" flat outlined class="filter-card">
        <v-card-text class="pa-3">
          <v-row>
            <!-- Primary Filters -->
            <v-col cols="12">
              <div class="mb-2 d-flex align-center">
                <v-icon small color="primary" class="mr-2">mdi-filter-variant</v-icon>
                <span class="text-subtitle-2 font-weight-medium">Primary Filters</span>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="2" sm="6" class="py-2">
              <v-select
                v-model="search_filter.sort"
                @change="filterChanged"
                :items="filter.sort_items"
                label="All Prices"
                clearable
                color="primary"
                hide-details
                outlined
                dense
              >
              </v-select>
            </v-col>
            <v-col
              v-for="(items, key) in filter.custom_fields"
              :key="'custom-' + key"
              cols="12"
              md="2"
              sm="6"
              class="py-2"
            >
              <v-autocomplete
                :value="search_filter.custom_fields ? search_filter.custom_fields[key] || [] : []"
                @change="
                  (val) => {
                    $set(search_filter.custom_fields, key, val)
                    filterChanged()
                  }
                "
                @input="
                  (val) => {
                    if (val !== undefined) $set(search_filter.custom_fields, key, val)
                  }
                "
                :items="items"
                :label="formatFieldKey(key)"
                color="primary"
                multiple
                outlined
                hide-details
                clearable
                dense
              >
                <template v-slot:item="{ active, item, attrs, on }">
                  <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
                    <v-list-item-action class="mr-3">
                      <v-checkbox :input-value="active"></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.text }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item, index }">
                  <v-chip color="primary" x-small v-if="index === 0" class="mt-1">
                    <span>{{ item.text }}</span>
                  </v-chip>
                  <span v-if="index === 1" class="grey--text text-caption ml-1">
                    (+{{ (search_filter.custom_fields[key] || []).length - 1 }} more)
                  </span>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="2" sm="6" class="py-2">
              <v-autocomplete
                @change="filterChanged"
                v-model="search_filter.brands"
                :items="filter.brands_items"
                label="Brands"
                color="primary"
                multiple
                outlined
                hide-details
                clearable
                dense
              >
                <template v-slot:item="{ active, item, attrs, on }">
                  <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
                    <v-list-item-action class="mr-3">
                      <v-checkbox :input-value="active"></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-row no-gutters align="center">
                          <span>{{ item.brand }}</span>
                          <v-spacer></v-spacer>
                          <v-chip v-if="item.count > 0" text-color="white" color="primary" small>{{
                            item.count
                          }}</v-chip>
                        </v-row>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>

                <template v-slot:selection="{ item, index }">
                  <v-chip color="primary" x-small v-if="index === 0" class="mt-1">
                    <span>{{ item.brand }}</span>
                  </v-chip>
                  <span v-if="index === 1" class="grey--text text-caption ml-1">
                    (+{{ search_filter.brands.length - 1 }} more)
                  </span>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="2" sm="6" class="py-2">
              <v-autocomplete
                v-model="search_filter.categories"
                :items="filter.categories_items"
                label="Categories"
                color="primary"
                multiple
                outlined
                hide-details
                clearable
                dense
              >
                <template v-slot:item="{ active, item, attrs, on }">
                  <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
                    <v-list-item-action class="mr-3">
                      <v-checkbox :input-value="active"></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-row no-gutters align="center">
                          <span>{{ item.category.toUpperCase() }}</span>
                          <v-spacer></v-spacer>
                          <v-chip v-if="item.count > 0" text-color="white" color="primary">{{
                            item.count
                          }}</v-chip>
                        </v-row>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item, index }">
                  <v-chip color="primary" x-small v-if="index === 0" class="mt-1">
                    <span>{{ item.category }}</span>
                  </v-chip>
                  <span v-if="index === 1" class="grey--text text-caption ml-1">
                    (+{{ search_filter.categories.length - 1 }} more)
                  </span>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" sm="6" class="py-2">
              <v-autocomplete
                v-model="search_filter.availabilities"
                @change="filterChanged"
                :items="filter.availability_items"
                label="My availabilities"
                color="primary"
                multiple
                outlined
                hide-details
                clearable
                dense
              >
                <template v-slot:item="{ active, item, attrs, on }">
                  <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
                    <v-list-item-action class="mr-3">
                      <v-checkbox :input-value="active"></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-row no-gutters align="center">
                          <badge
                            small="true"
                            :text="item.availability"
                            :color="
                              item.availability == 'InStock'
                                ? 'success'
                                : item.availability == 'NotFound'
                                ? 'grey'
                                : 'error'
                            "
                          />
                          <v-spacer></v-spacer>
                          <v-chip v-if="item.count > 0" text-color="white" color="primary" small>{{
                            item.count
                          }}</v-chip>
                        </v-row>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>

                <template v-slot:selection="{ item, index }">
                  <badge
                    v-if="index === 0"
                    small="true"
                    :text="item.availability"
                    :color="
                      item.availability == 'InStock'
                        ? 'success'
                        : item.availability == 'NotFound'
                        ? 'grey'
                        : 'error'
                    "
                  />
                  <span v-if="index === 1" class="grey--text text-caption ml-1">
                    (+{{ search_filter.availabilities.length - 1 }} more)
                  </span>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" sm="6" class="py-2" v-show="show_more_filters">
              <v-badge
                :value="search_filter.competitors_availabilities.length > 0"
                :content="search_filter.competitors_availabilities.length"
                color="primary"
                overlap
                offset-x="-5"
                offset-y="10"
              >
                <v-autocomplete
                  v-model="search_filter.competitors_availabilities"
                  @change="filterChanged"
                  :items="filter.competitors_availabilities_items"
                  label="Competitors availabilities"
                  color="primary"
                  multiple
                  outlined
                  hide-details
                  clearable
                  dense
                  :class="
                    search_filter.competitors_availabilities.length > 0 ? 'active-filter-input' : ''
                  "
                >
                  <template v-slot:prepend-inner>
                    <v-icon
                      small
                      :color="
                        search_filter.competitors_availabilities.length > 0 ? 'primary' : 'grey'
                      "
                      >mdi-store-check</v-icon
                    >
                  </template>
                  <template v-slot:item="{ active, item, attrs, on }">
                    <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
                      <v-list-item-action class="mr-3">
                        <v-checkbox :input-value="active"></v-checkbox>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          <v-row no-gutters align="center">
                            <badge
                              small="true"
                              :text="item.availability"
                              :color="
                                item.availability == 'InStock'
                                  ? 'success'
                                  : item.availability == 'NotFound'
                                  ? 'grey'
                                  : 'error'
                              "
                            />
                          </v-row>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>

                  <template v-slot:selection="{ item, index }">
                    <badge
                      v-if="index === 0"
                      small="true"
                      :text="item.availability"
                      :color="
                        item.availability == 'InStock'
                          ? 'success'
                          : item.availability == 'NotFound'
                          ? 'grey'
                          : 'error'
                      "
                    />
                    <span v-if="index === 1" class="grey--text text-caption ml-1">
                      (+{{ search_filter.competitors_availabilities.length - 1 }}
                      more)
                    </span>
                  </template>
                </v-autocomplete>
              </v-badge>
            </v-col>
          </v-row>

          <!-- More filters Section -->
          <v-row v-if="show_more_filters" class="mt-2">
            <v-col cols="12">
              <v-divider class="mb-3"></v-divider>
              <div class="mb-3 d-flex align-center">
                <v-icon small color="primary" class="mr-2">mdi-tune-variant</v-icon>
                <span class="text-subtitle-2 font-weight-medium">More filters</span>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="py-2">
              <div class="d-flex flex-wrap align-center">
                <v-menu
                  v-if="show_more_filters"
                  open-on-click
                  bottom
                  :close-on-content-click="false"
                  offset-y
                  v-model="menu_coefficient"
                >
                  <template v-slot:activator="{ on: menu }">
                    <v-chip color="default" outlined class="ma-1 filter-chip" v-on="{ ...menu }">
                      <v-icon left small>mdi-plus-circle-outline</v-icon>
                      <span>Prices</span>
                      <v-badge
                        v-if="search_filter.coefficient"
                        color="error"
                        content="1"
                        inline
                        class="ml-1"
                      ></v-badge>
                    </v-chip>
                  </template>
                  <v-card flat class="pa-0">
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" class="pa-0 pb-2 text-right">
                          <v-icon icon="start" small class="mr-2" @click="menu_coefficient = false"
                            >mdi-close-circle-outline</v-icon
                          >
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-select
                          v-model="search_filter.coefficient"
                          @change="filterChanged"
                          :items="filter.coefficient_items"
                          label="Prices"
                          clearable
                          color="secondary"
                          hide-details
                          outlined
                          dense
                        >
                        </v-select>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-menu>
                <v-menu
                  v-if="show_more_filters"
                  open-on-click
                  bottom
                  :close-on-content-click="false"
                  offset-y
                  v-model="menu_products"
                >
                  <template v-slot:activator="{ on: menu }">
                    <v-chip color="default" outlined class="ma-1 filter-chip" v-on="{ ...menu }">
                      <v-icon left small>mdi-plus-circle-outline</v-icon>
                      <span>Products</span>
                      <v-badge
                        v-if="search_filter.matched"
                        color="error"
                        content="1"
                        inline
                        class="ml-1"
                      ></v-badge>
                    </v-chip>
                  </template>
                  <v-card flat class="pa-0">
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" class="pa-0 pb-2 text-right">
                          <v-icon icon="start" small class="mr-2" @click="menu_products = false"
                            >mdi-close-circle-outline</v-icon
                          >
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-select
                          v-model="search_filter.matched"
                          @change="filterChanged"
                          :items="filter.matched_items"
                          label="Products"
                          clearable
                          color="secondary"
                          hide-details
                          outlined
                          dense
                        >
                        </v-select>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-menu>
                <v-menu
                  v-if="show_more_filters"
                  open-on-click
                  bottom
                  :close-on-content-click="false"
                  offset-y
                  v-model="menu_source"
                >
                  <template v-slot:activator="{ on: menu }">
                    <v-chip color="default" outlined class="ma-1 filter-chip" v-on="{ ...menu }">
                      <v-icon left small>mdi-plus-circle-outline</v-icon>
                      <span>Source</span>
                      <v-badge
                        v-if="search_filter.source"
                        color="error"
                        content="1"
                        inline
                        class="ml-1"
                      ></v-badge>
                    </v-chip>
                  </template>
                  <v-card flat class="pa-0">
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" class="pa-0 pb-2 text-right">
                          <v-icon icon="start" small class="mr-2" @click="menu_source = false"
                            >mdi-close-circle-outline</v-icon
                          >
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-select
                          v-model="search_filter.source"
                          @change="filterChanged"
                          :items="filter.source_items"
                          label="Source"
                          clearable
                          color="secondary"
                          hide-details
                          outlined
                          dense
                        >
                        </v-select>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-menu>
                <v-menu
                  v-if="show_more_filters"
                  open-on-click
                  bottom
                  :close-on-content-click="false"
                  v-model="menu_changes"
                  offset-y
                >
                  <template v-slot:activator="{ on: menu }">
                    <v-chip color="default" outlined class="ma-1 filter-chip" v-on="{ ...menu }">
                      <v-icon left small>mdi-plus-circle-outline</v-icon>
                      <span>Price changes</span>
                      <v-badge
                        v-if="search_filter.changes"
                        color="error"
                        content="1"
                        inline
                        class="ml-1"
                      ></v-badge>
                    </v-chip>
                  </template>
                  <v-card flat class="pa-0">
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" class="pa-0 pb-2 text-right">
                          <v-icon icon="start" small class="mr-2" @click="menu_changes = false"
                            >mdi-close-circle-outline</v-icon
                          >
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-select
                          v-model="search_filter.changes"
                          @change="filterChanged"
                          :items="filter.changes_items"
                          label="Changes"
                          clearable
                          color="secondary"
                          hide-details
                          outlined
                          dense
                        >
                        </v-select>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-menu>
                <v-menu
                  open-on-click
                  bottom
                  :close-on-content-click="false"
                  v-model="menu_competitors"
                  offset-y
                >
                  <template v-slot:activator="{ on: menu }">
                    <v-chip color="default" outlined class="ma-1 filter-chip" v-on="{ ...menu }">
                      <v-icon left small>mdi-plus-circle-outline</v-icon>
                      <span>Competitors</span>
                      <v-badge
                        v-if="search_filter.competitors_websites.length > 0"
                        color="error"
                        :content="search_filter.competitors_websites.length"
                        inline
                        class="ml-1"
                      ></v-badge>
                    </v-chip>
                  </template>
                  <v-card flat class="pa-0">
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" class="pa-0 pb-2 text-right">
                          <v-icon icon="start" small class="mr-2" @click="menu_competitors = false"
                            >mdi-close-circle-outline</v-icon
                          >
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-autocomplete
                          v-model="search_filter.competitors_websites"
                          @change="filterChanged"
                          :items="filter.competitors_websites_items"
                          label="Competitors"
                          color="secondary"
                          multiple
                          outlined
                          hide-details
                          clearable
                          dense
                        >
                          <template v-slot:item="{ active, item, attrs, on }">
                            <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
                              <v-list-item-action class="mr-3">
                                <v-checkbox :input-value="active"></v-checkbox>
                              </v-list-item-action>
                              <v-list-item-content>
                                <v-list-item-title>
                                  <v-row no-gutters align="center">
                                    <div class="d-flex align-center">
                                      <img
                                        :src="
                                          'https://8181.integ.crawlo.com/favicon?size=xs&website=' +
                                          item.competitor_website
                                        "
                                        @error="replaceByDefault"
                                        width="30"
                                        height="30"
                                        class="mr-2 favicon-img"
                                      />{{ item.competitor_website }}
                                    </div>
                                    <v-spacer></v-spacer>
                                    <v-chip
                                      v-if="item.count > 0"
                                      text-color="white"
                                      color="primary"
                                      small
                                      >{{ item.count }}</v-chip
                                    >
                                  </v-row>
                                </v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </template>

                          <template v-slot:selection="{ item, index }">
                            <v-chip color="primary" small v-if="index === 0">
                              <span>{{ item.competitor_website }}</span>
                            </v-chip>
                            <span v-if="index === 1" class="grey--text text-caption ml-1">
                              (+{{ search_filter.competitors_websites.length - 1 }} more)
                            </span>
                          </template>
                        </v-autocomplete>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-menu>
                <v-menu
                  open-on-click
                  bottom
                  :close-on-content-click="false"
                  v-model="menu_price_index"
                  offset-y
                >
                  <template v-slot:activator="{ on: menu }">
                    <v-chip color="default" outlined class="ma-1 filter-chip" v-on="{ ...menu }">
                      <v-icon left small>mdi-plus-circle-outline</v-icon>
                      <span>Price index</span>
                      <v-badge
                        v-if="search_filter.price_index.max || search_filter.price_index.min"
                        color="error"
                        content="1"
                        inline
                        class="ml-1"
                      ></v-badge>
                    </v-chip>
                  </template>
                  <v-card flat class="pa-0">
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" class="pa-0 pb-2 text-right">
                          <v-icon icon="start" small class="mr-2" @click="menu_price_index = false"
                            >mdi-close-circle-outline</v-icon
                          >
                        </v-col>
                      </v-row>
                      <v-row class="pa-0">
                        <v-col cols="6" class="d-flex justify-center pa-0 pr-1">
                          <v-text-field
                            label="Min"
                            style="width: 100px"
                            :max="0"
                            type="number"
                            v-model="search_filter.price_index.min"
                            dense
                            outlined
                            hide-details="auto"
                          >
                          </v-text-field>
                        </v-col>
                        <v-col cols="6" class="d-flex justify-center pa-0 pl-1">
                          <v-text-field
                            label="Max"
                            style="width: 100px"
                            type="number"
                            :min="0"
                            v-model="search_filter.price_index.max"
                            dense
                            outlined
                            hide-details="auto"
                          >
                          </v-text-field>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-menu>
                <v-menu
                  v-if="show_more_filters"
                  open-on-click
                  bottom
                  :close-on-content-click="false"
                  v-model="menu_similarity_score"
                  offset-y
                >
                  <template v-slot:activator="{ on: menu }">
                    <v-chip color="default" outlined class="ma-1 filter-chip" v-on="{ ...menu }">
                      <v-icon left small>mdi-plus-circle-outline</v-icon>
                      <span>Similarity Score</span>
                      <v-badge
                        v-if="
                          search_filter.similarity_score_min !== null &&
                          search_filter.similarity_score_min !== ''
                        "
                        color="error"
                        content="1"
                        inline
                        class="ml-1"
                      ></v-badge>
                    </v-chip>
                  </template>
                  <v-card flat class="pa-0">
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" class="pa-0 pb-2 text-right">
                          <v-icon
                            icon="start"
                            small
                            class="mr-2"
                            @click="menu_similarity_score = false"
                            >mdi-close-circle-outline</v-icon
                          >
                        </v-col>
                      </v-row>
                      <v-row class="pa-0">
                        <v-col cols="12" class="d-flex justify-center pa-0">
                          <v-text-field
                            label="Min similarity score"
                            style="width: 180px"
                            type="number"
                            :min="0"
                            :max="1"
                            step="0.01"
                            v-model="search_filter.similarity_score_min"
                            @change="filterChanged"
                            dense
                            outlined
                            hide-details="auto"
                          >
                          </v-text-field>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-menu>
                <v-chip
                  :color="show_more_filters ? 'primary' : 'default'"
                  :outlined="!show_more_filters"
                  @click="showMoreFilter()"
                  class="ma-1 filter-chip"
                >
                  <v-icon left small>
                    {{ show_more_filters ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                  {{ show_more_filters ? 'Show Less' : 'More filters' }}
                  <v-badge
                    v-if="is_applied_filters() > 0 && !show_more_filters"
                    color="error"
                    :content="is_applied_filters()"
                    inline
                    class="ml-1"
                  ></v-badge>
                </v-chip>
              </div>
            </v-col>
          </v-row>
          <!-- Applied Filters Summary -->
          <v-row v-if="applied_filters.length > 0" class="mt-3">
            <v-col cols="12">
              <v-divider class="mb-3"></v-divider>
              <div class="mb-3 d-flex align-center justify-space-between">
                <div class="d-flex align-center">
                  <v-icon small color="success" class="mr-2">mdi-checkbox-marked-circle</v-icon>
                  <span class="text-subtitle-2 font-weight-medium">Active Filter Criteria</span>
                  <v-chip x-small color="success" text-color="white" class="ml-2">
                    {{ applied_filters.length }}
                  </v-chip>
                </div>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      text
                      x-small
                      color="error"
                      @click="clearAppliedFilters"
                    >
                      <v-icon left x-small>mdi-close-circle</v-icon>
                      Clear All
                    </v-btn>
                  </template>
                  <span>Remove all applied filter criteria</span>
                </v-tooltip>
              </div>
            </v-col>
          </v-row>
          <v-row v-if="applied_filters.length > 0">
            <v-col cols="12">
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="item in applied_filters"
                  :key="item.filter"
                  class="ma-1"
                  color="success"
                  text-color="white"
                  small
                  close
                  @click:close="resetFilter(item.filter)"
                >
                  <strong>{{ item.filter }}</strong
                  >: {{ item.value }}
                </v-chip>
              </div>
            </v-col>
          </v-row>

          <!-- Global Action Bar -->
          <v-row class="mt-4">
            <v-col cols="12">
              <v-divider class="mb-4"></v-divider>
              <div class="d-flex align-center justify-space-between action-bar flex-wrap">
                <div class="text-caption text--secondary py-2">
                  <v-icon small color="grey" class="mr-1">mdi-information-outline</v-icon>
                  {{
                    exportation
                      ? 'Click "Preview" to see matching products'
                      : 'Click "Apply Filters" to update the results'
                  }}
                </div>
                <div class="d-flex">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        color="grey darken-1"
                        class="mr-3 text-none"
                        @click="resetDashboard"
                        small
                      >
                        <v-icon left small>mdi-refresh</v-icon>
                        Reset All
                      </v-btn>
                    </template>
                    <span>Clear all filters and reset to default</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        color="primary"
                        class="text-none"
                        @click="sumbitFilter"
                        elevation="2"
                        small
                      >
                        <v-icon left small>{{
                          exportation ? 'mdi-eye-outline' : 'mdi-filter-check'
                        }}</v-icon>
                        {{ exportation ? 'Preview' : 'Apply Filters' }}
                      </v-btn>
                    </template>
                    <span>{{
                      exportation
                        ? 'Preview products matching the selected filters'
                        : 'Apply selected filters to update results'
                    }}</span>
                  </v-tooltip>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-expand-transition>
  </div>
</template>
<script>
  import axios from 'axios'
  export default {
    name: 'filterInputs',
    filters: {
      truncate(value, length) {
        if (!value) return ''
        if (value.length <= length) return value
        return value.substring(0, length) + '...'
      },
    },
    props: ['component_name', 'exportation', 'opened'],
    data() {
      return {
        menu: false,
        menu_products: false,
        menu_coefficient: false,
        menu_source: false,
        menu_changes: false,
        menu_competitors: false,
        menu_price_index: false,
        menu_similarity_score: false,
        applied_filters: [],
        filter: {
          sort_items: ['Highest (absolute)', 'Higher', 'Equal', 'Lowest (absolute)', 'Lower'],
          matched_items: ['Matched', 'Not Matched', 'Validated', 'Not Validated'],
          source_items: ['Google shopping', 'Direct crawling'],
          coefficient_items: ['All prices', 'Coefficient prices'],
          changes_items: ['Today', 'Last 7 days', 'Last 30 days'],
          competitors_websites_items: [],
          competitors_availability_items: [],
          availability_items: [],
          brands_items: [],
          categories_items: [],
          index: [-100, 100],
          price_index: {
            min: null,
            max: null,
          },
        },
        search_filter: {
          key: '',
          sort: '',
          matched: '',
          coefficient: '',
          source: '',
          changes: '',
          competitors_websites: [],
          competitors_availabilities: [],
          availabilities: [],
          brands: [],
          categories: [],
          price_index: {
            min: null,
            max: null,
          },
          similarity_score_min: null,
        },
        show_filter: true,
        show_more_filters: false,
      }
    },
    methods: {
      getTotalActiveFilters() {
        let count = 0
        if (this.search_filter.key) count++
        if (this.search_filter.sort) count++
        if (this.search_filter.matched) count++
        if (this.search_filter.coefficient) count++
        if (this.search_filter.source) count++
        if (this.search_filter.changes) count++
        if (this.search_filter.brands.length > 0) count++
        if (this.search_filter.categories.length > 0) count++
        if (this.search_filter.availabilities.length > 0) count++
        if (this.search_filter.competitors_websites.length > 0) count++
        if (this.search_filter.competitors_availabilities.length > 0) count++
        if (
          this.search_filter.price_index.min !== null ||
          this.search_filter.price_index.max !== null
        )
          count++
        if (
          this.search_filter.similarity_score_min !== null &&
          this.search_filter.similarity_score_min !== ''
        )
          count++
        Object.values(this.search_filter.custom_fields || {}).forEach((val) => {
          if (val && val.length > 0) count++
        })
        return count
      },
      clearAppliedFilters() {
        this.applied_filters = []
        this.search_filter.price_index.min = null
        this.search_filter.price_index.max = null
        this.filterChanged()
      },
      is_applied_filters() {
        let number_of_applied_filters = 0
        if (this.search_filter.matched) {
          number_of_applied_filters++
        }
        if (this.search_filter.source) {
          number_of_applied_filters++
        }

        if (this.search_filter.changes) {
          number_of_applied_filters++
        }

        if (this.search_filter.coefficient) {
          number_of_applied_filters++
        }

        if (
          this.search_filter.similarity_score_min !== null &&
          this.search_filter.similarity_score_min !== ''
        ) {
          number_of_applied_filters++
        }

        return number_of_applied_filters
      },
      resetCompetitors() {
        this.search_filter.competitors_websites = []
      },
      resetPriceIndex() {
        this.search_filter.price_index.min = null
        this.search_filter.price_index.max = null
      },
      resetFilter(filter = '') {
        switch (filter) {
          case 'index':
            this.filter.price_index.min = null
            this.filter.price_index.max = null
            this.applied_filters = this.applied_filters.filter((elt) => elt.filter !== 'index')
            break
          default:
            break
        }
      },
      apply(filter = '') {
        switch (filter) {
          case 'index':
            if (this.filter.price_index.min == null || this.filter.price_index.max == null) return
            this.search_filter.price_index.min = this.filter.price_index.min
            this.search_filter.price_index.max = this.filter.price_index.max
            if (this.applied_filters.filter((elt) => elt.filter === 'index').length === 0) {
              this.applied_filters.push({
                filter: 'index',
                value: `${this.filter.price_index.min} - ${this.filter.price_index.max}`,
              })
            } else {
              this.applied_filters = this.applied_filters.map((elt) => {
                if (elt.filter === 'index') {
                  elt.value = `${this.filter.price_index.min} - ${this.filter.price_index.max}`
                }
                return elt
              })
            }

            break
          default:
            break
        }
        this.menu = !this.menu
      },
      showMoreFilter() {
        this.show_more_filters = !this.show_more_filters
      },
      filterChanged() {
        this.$emit('filter-changed', this.search_filter)
      },
      showFilters() {
        this.show_filter = !this.show_filter
        this.clearImputs()
      },
      sumbitFilter() {
        this.show_more_filters = false
        //cloase all menus
        this.menu = false
        this.menu_products = false
        this.menu_source = false
        this.menu_changes = false
        this.menu_competitors = false
        this.menu_price_index = false
        this.menu_similarity_score = false
        this.$emit('load-data', this.search_filter)
      },
      resetDashboard() {
        this.clearImputs()
        this.search_filter.reset = true
        this.show_more_filters = false
        this.$emit('load-data', this.search_filter)
      },
      loadFilterFromStorage() {
        const saved = localStorage.getItem('filter')
        if (saved) {
          const parsed = JSON.parse(saved)
          this.search_filter = {
            ...this.search_filter,
            ...parsed,
            custom_fields: parsed.custom_fields || {},
          }
        }
        this.$nextTick(() => {
          this.sumbitFilter()
        })
      },
      clearImputs() {
        this.search_filter.availabilities = []
        this.search_filter.competitors_websites = []
        this.search_filter.competitors_availabilities = []
        this.search_filter.brands = []
        this.search_filter.categories = []
        this.search_filter.sort = ''
        this.search_filter.key = ''
        this.search_filter.matched = ''
        this.search_filter.source = ''
        this.search_filter.price_index.min = null
        this.search_filter.price_index.max = null
        this.search_filter.changes = ''
        this.search_filter.coefficient = ''
        this.search_filter.similarity_score_min = null
        if (!this.search_filter.custom_fields) {
          this.$set(this.search_filter, 'custom_fields', {})
        }
        Object.keys(this.search_filter.custom_fields || {}).forEach((key) => {
          this.$set(this.search_filter.custom_fields, key, [])
        })
        this.applied_filters = []
        localStorage.removeItem('filter')
      },
      async getData() {
        await axios
          .get('/api/products/getfilterdata', {
            params: {
              account: localStorage.getItem('account_id'),
              user: localStorage.getItem('user_id'),
              component: this.component_name,
            },
          })
          .then((res) => {
            this.filter.brands_items = res.data.brands
              .map((elt) => {
                elt.text = elt.brand
                return elt
              })
              .filter((elt) => elt.brand.length)
              //remove dups
              .reduce((acc, current) => {
                const x = acc.find((item) => item.brand === current.brand)
                if (!x) {
                  return acc.concat([current])
                } else {
                  return acc
                }
              }, [])

            this.filter.categories_items = res.data.categories
              .map((elt) => {
                elt.text = elt.category
                return elt
              })
              .filter((elt) => elt.category.length)

            this.filter.availability_items = res.data.availabilities
              .map((elt) => {
                elt.text = elt.availability
                return elt
              })
              .filter((elt) => elt.availability.length)
            this.filter.availability_items.push({
              availability: 'NotFound',
              text: 'NotFound',
              count: 0,
            })

            this.filter.competitors_websites_items = res.data.competitors_websites
              .map((elt) => {
                elt.text = elt.competitor_website
                return elt
              })
              .filter((elt) => elt.competitor_website.length)

            this.filter.competitors_availabilities_items = res.data.competitors_availabilities
              .map((elt) => {
                elt.text = elt.availability
                return elt
              })
              .filter((elt) => elt.availability.length)

            this.filter.competitors_availabilities_items.push({
              availability: 'NotFound',
              text: 'NotFound',
              count: 0,
            })

            if (
              res.data.customFields &&
              res.data.customFields.meta &&
              res.data.customFields.meta.customFields
            ) {
              const grouped = {}
              res.data.customFields.meta.customFields.forEach((field) => {
                if (!grouped[field.key]) {
                  grouped[field.key] = []
                }
                if (!grouped[field.key].find((item) => item.value === field.value)) {
                  grouped[field.key].push({ text: field.label, value: field.value })
                }
              })
              this.filter.custom_fields = grouped
              if (!this.search_filter.custom_fields) {
                this.$set(this.search_filter, 'custom_fields', {})
              }
              Object.keys(grouped).forEach((key) => {
                if (!this.search_filter.custom_fields[key]) {
                  this.$set(this.search_filter.custom_fields, key, [])
                }
              })
            }
          })
      },
      setMatchedFilter(value) {
        this.search_filter.matched = value
        this.$emit('load-data', this.search_filter)
        localStorage.setItem('filter', JSON.stringify(this.search_filter))
      },
      formatFieldKey(key) {
        return key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim()
      },
    },
    computed: {},
    mounted() {
      this.getData()
      if (localStorage.getItem('filter')) {
        const saved = JSON.parse(localStorage.getItem('filter'))
        this.search_filter = {
          ...this.search_filter,
          ...saved,
          custom_fields: saved.custom_fields || {},
        }
      }
    },
    watch: {
      search_filter: {
        handler() {
          /**
           * save the filter in the local storage
           */
          localStorage.setItem('filter', JSON.stringify(this.search_filter))
        },
        deep: true,
      },
      opened: {
        handler() {
          if (this.exportation) {
            this.loadFilterFromStorage()
          } else {
            this.resetDashboard()
          }
        },
      },
    },
  }
</script>
<style scoped>
  .filter-container {
    margin-bottom: 16px;
  }

  .filter-card {
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    transition: box-shadow 0.3s ease;
  }

  .filter-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  /* Filter Toggle Bar */
  .filter-toggle-bar {
    padding: 4px 0;
    border-radius: 6px;
    transition: all 0.3s ease;
  }

  .filter-toggle-bar--collapsed {
    background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px dashed #90caf9;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .filter-toggle-bar--collapsed:hover {
    background: linear-gradient(135deg, #bbdefb 0%, #e1bee7 100%);
    border-color: #42a5f5;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15);
  }

  .filter-toggle-btn--collapsed {
    font-weight: 600 !important;
  }

  /* Collapsed filters summary */
  .collapsed-filters-summary {
    background: #fafafa;
    border-radius: 6px;
    padding: 6px 10px;
    border: 1px solid #eeeeee;
  }

  /* Hint text when collapsed and no filters */
  .collapsed-filters-hint {
    padding: 4px 8px;
    cursor: pointer;
    border-radius: 4px;
    transition: background 0.2s ease;
  }

  .collapsed-filters-hint:hover {
    background: #f5f5f5;
  }

  /* Pulse animation for filter icon when active filters are hidden */
  @keyframes filter-pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.3);
    }
    100% {
      transform: scale(1);
    }
  }

  .filter-icon-pulse {
    animation: filter-pulse 2s ease-in-out infinite;
  }

  /* Active filter styling */
  .active-filter-input {
    border: 2px solid currentColor !important;
  }

  .active-filter-input >>> .v-input__slot {
    border-color: var(--v-primary-base) !important;
  }

  /* Filter chip styling */
  .filter-chip {
    cursor: pointer;
    font-weight: 500;
  }

  /* Action bar styling */
  .action-bar {
    padding: 8px 0;
    gap: 16px;
  }

  .action-bar .v-btn {
    min-width: 150px;
  }

  @media (max-width: 960px) {
    .action-bar {
      flex-direction: column;
      align-items: stretch !important;
    }

    .action-bar .v-btn {
      width: 100%;
      margin: 4px 0 !important;
    }
  }

  /* Search bar enhancements */
  .v-input--is-focused.active-filter-input >>> .v-input__slot {
    box-shadow: 0 0 0 3px rgba(var(--v-primary-base), 0.2);
  }

  /* Smooth transitions */
  .v-expand-transition-enter-active,
  .v-expand-transition-leave-active {
    transition: all 0.3s ease-in-out;
  }

  /* Badge transitions */
  .v-badge__badge {
    transition: all 0.3s ease;
  }

  /* Applied filters styling - simplified */
  .v-chip.success {
    transition: opacity 0.2s ease;
  }

  .v-chip.success:hover {
    opacity: 0.9;
  }

  /* Responsive adjustments */
  @media (max-width: 600px) {
    .filter-status-bar {
      flex-direction: column;
      align-items: flex-start !important;
    }

    .filter-toggle-bar--collapsed {
      flex-direction: column;
      align-items: flex-start !important;
      gap: 8px;
    }

    .collapsed-filters-summary .d-flex {
      gap: 2px;
    }
  }

  .btn-min-width {
    min-width: 130px !important;
  }

  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    appearance: textfield;
    -moz-appearance: textfield;
  }

  /* Favicon styling */
  .favicon-img {
    border-radius: 4px;
  }
</style>
